// Email templates editor (Phase 5c). List + Monaco-based editor + sync.

import { useEffect, useMemo, useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCustomCellProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import Editor from '@monaco-editor/react';
import type { EmailTemplateCreateRequest, EmailTemplateDto, EmailTemplateUpdateRequest, RegionSyncResult } from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { adminApiFetch } from '../../lib/adminApiClient';

const UpdatedCell = (props: GridCustomCellProps) => (
  <td {...props.tdProps}>{formatDateTime((props.dataItem as EmailTemplateDto).updatedUtc)}</td>
);

export function EmailTemplates() {
  const [rows, setRows] = useState<EmailTemplateDto[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => rows?.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  );

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [seededId, setSeededId] = useState<string | null>(null);

  // Seed the edit buffer when a different template becomes selected. Done during
  // render (React's "adjust state when something changes" pattern) instead of in
  // an effect, to avoid the cascading-render setState-in-effect anti-pattern.
  if (selected && selected.id !== seededId) {
    setSeededId(selected.id);
    setSubject(selected.subject);
    setBodyHtml(selected.bodyHtml);
    setBodyText(selected.bodyText);
  }

  const reload = async () => {
    const data = await adminApiFetch<EmailTemplateDto[]>('/api/admin/email-templates');
    setRows(data);
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      const data = await adminApiFetch<EmailTemplateDto[]>('/api/admin/email-templates');
      if (active) setRows(data);
    })();
    return () => {
      active = false;
    };
  }, []);

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const body: EmailTemplateUpdateRequest = { subject, bodyHtml, bodyText };
      const updated = await adminApiFetch<EmailTemplateDto>(
        `/api/admin/email-templates/${selected.id}`,
        { method: 'PUT', body },
      );
      setRows((current) => current?.map((r) => (r.id === updated.id ? updated : r)) ?? null);
      setToast('Saved.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const onSync = async () => {
    if (!selected) return;
    try {
      const result = await adminApiFetch<RegionSyncResult>(
        `/api/admin/email-templates/${selected.id}/sync`,
        { method: 'POST' },
      );
      const summary = result.regions.map((r) => `${r.region.toUpperCase()}: ${r.status}`).join(' · ');
      setToast(`Sync: ${summary}`);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Sync failed.');
    }
  };

  const onNew = async () => {
    const key = window.prompt('Template key (e.g. tenant.welcome)');
    if (!key) return;
    try {
      const body: EmailTemplateCreateRequest = {
        key,
        subject: 'New template subject',
        bodyHtml: '<p>Hello @Model.Name</p>',
        bodyText: 'Hello @Model.Name',
      };
      const created = await adminApiFetch<EmailTemplateDto>('/api/admin/email-templates', {
        method: 'POST',
        body,
      });
      await reload();
      setSelectedId(created.id);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Create failed.');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16, height: 'calc(100vh - 120px)' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Email templates</h2>
          <Button size="small" onClick={() => void onNew()}>New</Button>
        </div>
        <Grid
          data={rows ?? []}
          onRowClick={(e) => setSelectedId((e.dataItem as EmailTemplateDto).id)}
          style={{ height: 'calc(100vh - 200px)' }}
        >
          <GridColumn field="key" title="Key" />
          <GridColumn field="updatedUtc" title="Updated" width="160px" cells={{ data: UpdatedCell }} />
        </Grid>
      </div>
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(15,23,42,.06)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!selected && <div style={{ color: '#64748b' }}>Pick a template from the left to edit.</div>}
        {selected && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{selected.key}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>Last updated by {selected.updatedBy} @ {formatDateTime(selected.updatedUtc)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={() => void onSync()}>Sync to all regions</Button>
                <Button themeColor="primary" disabled={saving} onClick={() => void onSave()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ padding: 10, border: '1px solid #cbd5e1', borderRadius: 8 }} />
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>HTML body (Razor)</label>
            <div style={{ border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }}>
              <Editor
                height="320px"
                language="html"
                value={bodyHtml}
                onChange={(v) => setBodyHtml(v ?? '')}
                options={{ minimap: { enabled: false }, fontSize: 12 }}
              />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Plain-text body</label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              style={{ minHeight: 120, padding: 10, border: '1px solid #cbd5e1', borderRadius: 8, fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12 }}
            />
          </>
        )}
      </div>

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {toast && (
          <Notification type={{ style: 'info', icon: true }} closable onClose={() => setToast(null)}>
            <span>{toast}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}
